import { TrendingDownIcon, TrendingUpIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { OverzichtData, Vergelijking } from './overzicht.api';

/** Zonder vorige periode om tegen af te zetten is er geen trend: delen door
 *  nul is geen "oneindig omhoog", het is niets om mee te vergelijken. */
function trend({ nu, vorige }: Vergelijking): number | null {
  if (vorige === 0) return null;
  return Math.round(((nu - vorige) / vorige) * 100);
}

/**
 * Eén kaart volgens shadcn's `section-cards`: label boven, getal groot met
 * `tabular-nums`, de trend als badge rechtsboven en één uitleggende regel
 * onderin. De container-query maakt het getal groter zodra de kaart breed
 * genoeg is.
 */
function Kaart({
  label,
  waarde,
  delta,
  vorigeLabel,
}: {
  label: string;
  waarde: string | number;
  delta: number | null;
  vorigeLabel: string;
}) {
  const stijgt = delta !== null && delta >= 0;
  const Icoon = stijgt ? TrendingUpIcon : TrendingDownIcon;

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardDescription>{label}</CardDescription>
        <CardTitle className="text-2xl font-semibold tabular-nums @[250px]/card:text-3xl">
          {waarde}
        </CardTitle>
        {delta !== null && (
          <CardAction>
            <Badge variant="outline">
              <Icoon />
              {delta > 0 ? '+' : ''}
              {delta}%
            </Badge>
          </CardAction>
        )}
      </CardHeader>
      <CardFooter className="flex-col items-start gap-1.5 text-sm">
        {delta === null ? (
          <div className="text-muted-foreground">Geen {vorigeLabel} om mee te vergelijken</div>
        ) : (
          <div className="line-clamp-1 flex gap-2 font-medium">
            {delta === 0 ? 'Gelijk gebleven' : stijgt ? 'Meer dan' : 'Minder dan'}{' '}
            {delta !== 0 && vorigeLabel} <Icoon className="size-4" />
          </div>
        )}
      </CardFooter>
    </Card>
  );
}

export function KpiKaarten({ data }: { data: OverzichtData }) {
  const { kpi, vorige } = data;

  return (
    <div className="grid grid-cols-1 gap-4 @xl/hoofd:grid-cols-2 @5xl/hoofd:grid-cols-4">
      <Kaart
        delta={trend(kpi.gesprekken)}
        label="Gesprekken geüpload"
        vorigeLabel={vorige.label}
        waarde={kpi.gesprekken.nu}
      />
      <Kaart
        delta={trend(kpi.buddies)}
        label="Buddy's die uploadden"
        vorigeLabel={vorige.label}
        waarde={`${kpi.buddies.nu} van ${kpi.buddies.totaal}`}
      />
      <Kaart
        delta={trend(kpi.hulpvragen)}
        label="Hulpvragen gevonden"
        vorigeLabel={vorige.label}
        waarde={kpi.hulpvragen.nu}
      />
      <Kaart
        delta={trend(kpi.nieuweOnderwerpen)}
        label="Nieuwe onderwerpen"
        vorigeLabel={vorige.label}
        waarde={kpi.nieuweOnderwerpen.nu}
      />
    </div>
  );
}
